/**
 * OrbitingCursor - Custom cursor that rotates to point at the anchor atom
 *
 * Displays a visual cursor that rotates around the anchor atom's position,
 * providing feedback during anchor orbit interactions.
 */

import { useMemo, memo } from 'react';

// Default SVG markup for the cursor (can be customized)
const DEFAULT_ORBIT_CURSOR_SVG = `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M28.0697 13.8667C28.0697 13.8667 25.9093 16.6745 23.8758 18.0606C21.0936 19.9571 19.1173 20.9503 15.7502 20.9439C12.2518 20.9372 10.2176 19.82 7.36241 17.7985C5.47205 16.46 3.43065 13.8667 3.43065 13.8667M28.0697 13.8667L21.7789 14.3909M28.0697 13.8667L27.5454 20.1575M3.43065 13.8667L9.72146 14.3909M3.43065 13.8667L3.95489 20.1575" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M28.0697 13.8667C28.0697 13.8667 25.9093 16.6745 23.8758 18.0606C21.0936 19.9571 19.1173 20.9503 15.7502 20.9439C12.2518 20.9372 10.2176 19.82 7.36241 17.7985C5.47205 16.46 3.43065 13.8667 3.43065 13.8667M28.0697 13.8667L21.7789 14.3909M28.0697 13.8667L27.5454 20.1575M3.43065 13.8667L9.72146 14.3909M3.43065 13.8667L3.95489 20.1575" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`;

export interface OrbitingCursorProps {
  /** Current mouse position in screen coordinates */
  mousePos: { x: number; y: number };
  /** Anchor atom center position in screen coordinates */
  anchorScreenPos: { x: number; y: number } | null;
  /** Whether cursor is visible (when hovering in the container) */
  isVisible: boolean;
  /** Whether currently dragging (affects transition smoothness) */
  isDragging: boolean;
  /** Custom SVG markup for the cursor (optional) */
  cursorSvg?: string;
}

/**
 * Software cursor that rotates to point at the anchor atom
 * Memoized to prevent unnecessary re-renders
 */
export const OrbitingCursor = memo(function OrbitingCursor({
  mousePos,
  anchorScreenPos,
  isVisible,
  isDragging,
  cursorSvg = DEFAULT_ORBIT_CURSOR_SVG,
}: OrbitingCursorProps) {
  // Calculate angle from cursor to anchor (cursor "points at" the anchor)
  // The cursor "faces up" by default (0° = up), so we add 90° to make it point correctly
  // Memoized to avoid recalculation on unrelated re-renders
  const angle = useMemo(() => {
    return anchorScreenPos
      ? Math.atan2(anchorScreenPos.y - mousePos.y, anchorScreenPos.x - mousePos.x) * (180 / Math.PI) + 90
      : 0;
  }, [anchorScreenPos, mousePos]);

  if (!isVisible || !anchorScreenPos) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        left: mousePos.x,
        top: mousePos.y,
        width: 32,
        height: 32,
        marginLeft: -16,
        marginTop: -16,
        pointerEvents: 'none',
        zIndex: 99999,
        transform: `rotate(${angle}deg)`,
        // Smooth transition when not dragging, instant when dragging
        transition: isDragging ? 'none' : 'transform 0.08s ease-out',
        willChange: 'transform, left, top',
      }}
      dangerouslySetInnerHTML={{ __html: cursorSvg }}
    />
  );
});
